import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import {
  GoalLevel,
  GoalOutcome,
  GoalStatus,
  OneOnOneMeetingStatus,
  ReviewCycleKind,
  ReviewCycleStatus,
  ReviewStatus,
  ReviewSubmissionStatus,
} from '@gokrew/models/performance';
import { Tone } from '@gokrew/models/ui.types';
import { Chip } from '@/components/Chip';
import styles from './PerformanceChips.module.css';

const REVIEW_TONE: Record<ReviewStatus, Tone> = {
  [ReviewStatus.NOT_STARTED]: Tone.SOFT_GRAY,
  [ReviewStatus.IN_PROGRESS]: Tone.WARNING,
  [ReviewStatus.READY_TO_SHARE]: Tone.PRIMARY,
  [ReviewStatus.SHARED]: Tone.ACCENT,
  [ReviewStatus.ACKNOWLEDGED]: Tone.SUCCESS,
};

const SUBMISSION_TONE: Record<ReviewSubmissionStatus, Tone> = {
  [ReviewSubmissionStatus.NOT_STARTED]: Tone.SOFT_GRAY,
  [ReviewSubmissionStatus.IN_PROGRESS]: Tone.WARNING,
  [ReviewSubmissionStatus.SUBMITTED]: Tone.SUCCESS,
};

const CYCLE_TONE: Record<ReviewCycleStatus, Tone> = {
  [ReviewCycleStatus.DRAFT]: Tone.SOFT_GRAY,
  [ReviewCycleStatus.ACTIVE]: Tone.SUCCESS,
  [ReviewCycleStatus.LOCKED]: Tone.SECONDARY,
};

const KIND_TONE: Record<ReviewCycleKind, Tone> = {
  [ReviewCycleKind.ANNUAL]: Tone.PRIMARY,
  [ReviewCycleKind.MID_YEAR]: Tone.ACCENT,
  [ReviewCycleKind.QUARTERLY]: Tone.SECONDARY,
  [ReviewCycleKind.PROBATION]: Tone.WARNING,
  [ReviewCycleKind.AD_HOC]: Tone.SOFT_GRAY,
  [ReviewCycleKind.UPWARD]: Tone.SOFT_BLUE,
};

const GOAL_STATUS_TONE: Record<GoalStatus, Tone> = {
  [GoalStatus.ON_TRACK]: Tone.SUCCESS,
  [GoalStatus.AT_RISK]: Tone.WARNING,
  [GoalStatus.OFF_TRACK]: Tone.ERROR,
};

const GOAL_OUTCOME_TONE: Record<GoalOutcome, Tone> = {
  [GoalOutcome.ACHIEVED]: Tone.SUCCESS,
  [GoalOutcome.NOT_ACHIEVED]: Tone.SECONDARY,
  [GoalOutcome.CANCELLED]: Tone.SOFT_GRAY,
};

const GOAL_LEVEL_TONE: Record<GoalLevel, Tone> = {
  [GoalLevel.COMPANY]: Tone.PRIMARY,
  [GoalLevel.TEAM]: Tone.ACCENT,
  [GoalLevel.INDIVIDUAL]: Tone.SOFT_BLUE,
};

const MEETING_TONE: Record<OneOnOneMeetingStatus, Tone> = {
  [OneOnOneMeetingStatus.UPCOMING]: Tone.PRIMARY,
  [OneOnOneMeetingStatus.COMPLETED]: Tone.SUCCESS,
  [OneOnOneMeetingStatus.SKIPPED]: Tone.SOFT_GRAY,
};

interface StatusChipProps<T extends string> {
  status: T;
  className?: string;
}

/** Where one reviewee's review stands (derived by the backend). */
export const ReviewStatusChip = ({
  status,
  className,
}: StatusChipProps<ReviewStatus>) => {
  const { t } = useTranslation(TranslationNS.Performance);
  return (
    <Chip
      type={REVIEW_TONE[status] ?? Tone.SOFT_GRAY}
      size="md"
      showDot
      className={className ?? styles.chip}
    >
      {t(`shared.reviewStatus.${status}`)}
    </Chip>
  );
};

/** One reviewer's progress on one review. */
export const SubmissionStatusChip = ({
  status,
  className,
}: StatusChipProps<ReviewSubmissionStatus>) => {
  const { t } = useTranslation(TranslationNS.Performance);
  return (
    <Chip
      type={SUBMISSION_TONE[status] ?? Tone.SOFT_GRAY}
      size="md"
      showDot
      className={className ?? styles.chip}
    >
      {t(`shared.submissionStatus.${status}`)}
    </Chip>
  );
};

export const CycleStatusChip = ({
  status,
  className,
}: StatusChipProps<ReviewCycleStatus>) => {
  const { t } = useTranslation(TranslationNS.Performance);
  return (
    <Chip
      type={CYCLE_TONE[status] ?? Tone.SOFT_GRAY}
      size="md"
      showDot
      className={className ?? styles.chip}
    >
      {t(`shared.cycleStatus.${status}`)}
    </Chip>
  );
};

/** What a cycle is for, set from its template (PF-9). */
export const CycleKindChip = ({
  kind,
  className,
}: {
  kind: ReviewCycleKind;
  className?: string;
}) => {
  const { t } = useTranslation(TranslationNS.Performance);
  return (
    <Chip
      type={KIND_TONE[kind] ?? Tone.SOFT_GRAY}
      size="md"
      className={className ?? styles.chip}
    >
      {t(`shared.cycleKind.${kind}`)}
    </Chip>
  );
};

export const MeetingStatusChip = ({
  status,
  className,
}: StatusChipProps<OneOnOneMeetingStatus>) => {
  const { t } = useTranslation(TranslationNS.Performance);
  return (
    <Chip
      type={MEETING_TONE[status] ?? Tone.SOFT_GRAY}
      size="md"
      className={className ?? styles.chip}
    >
      {t(`shared.meetingStatus.${status}`)}
    </Chip>
  );
};

/** On track, at risk or off track (PF-15). */
export const GoalStatusChip = ({
  status,
  className,
}: StatusChipProps<GoalStatus>) => {
  const { t } = useTranslation(TranslationNS.Performance);
  return (
    <Chip
      type={GOAL_STATUS_TONE[status] ?? Tone.SOFT_GRAY}
      size="md"
      showDot
      className={className ?? styles.chip}
    >
      {t(`shared.goal.status.${status}`)}
    </Chip>
  );
};

/** How a closed goal ended (PF-15). */
export const GoalOutcomeChip = ({
  status,
  className,
}: StatusChipProps<GoalOutcome>) => {
  const { t } = useTranslation(TranslationNS.Performance);
  return (
    <Chip
      type={GOAL_OUTCOME_TONE[status] ?? Tone.SOFT_GRAY}
      size="md"
      className={className ?? styles.chip}
    >
      {t(`shared.goal.outcome.${status}`)}
    </Chip>
  );
};

/** Company, team or individual (PF-14). */
export const GoalLevelChip = ({
  status,
  className,
}: StatusChipProps<GoalLevel>) => {
  const { t } = useTranslation(TranslationNS.Performance);
  return (
    <Chip
      type={GOAL_LEVEL_TONE[status] ?? Tone.SOFT_GRAY}
      size="md"
      className={className ?? styles.chip}
    >
      {t(`shared.goal.level.${status}`)}
    </Chip>
  );
};
